import { toPlayback } from "~/features/detail/playback"
import type { DetailSession, DetailView } from "~/features/detail/types"
import type { GetStudyByIdQuery } from "~/graphql/generated/graphql"
import { formatAuthors, formatDuration, formatMetaLine, progressPercent } from "~/shared/lib/formatting"

type Study = NonNullable<GetStudyByIdQuery["studyByID"]>
type StudySession = NonNullable<Study["sessions"]>[number]

const UNTITLED_STUDY = "Untitled study"

/** `videoSources` is a union: a `NoAccessError` carries no duration. */
const durationMillisecondsOf = (session: StudySession): number | undefined =>
  session.video?.videoSources?.__typename === "VideoSources"
    ? (session.video.videoSources.durationMilliseconds ?? undefined)
    : undefined

const toSession = (studyId: string, session: StudySession, index: number): DetailSession => {
  const video = session.video
  const durationMilliseconds = durationMillisecondsOf(session)

  return {
    id: session.id,
    title: video?.title?.trim() || `Session ${index + 1}`,
    description: video?.description?.trim() || undefined,
    thumbnailUrl: video?.thumbnailUrl ?? undefined,
    duration: formatDuration(durationMilliseconds),
    progressPercent: progressPercent(video?.progress?.playheadSeconds, durationMilliseconds),
    href: `/study/${studyId}/session/${session.id}`,
    playback: toPlayback(video),
  }
}

/**
 * The most recently played session, matching the TV app's resume behaviour
 * (`useLastPlayedSession`) — the graph orders `sessions` by sequence, not by
 * recency, so the pick has to come from `progress.lastUpdate`.
 */
const lastPlayedIndex = (sessions: readonly StudySession[]): number => {
  let best = -1
  let bestTime = 0

  sessions.forEach((session, index) => {
    const lastUpdate = session.video?.progress?.lastUpdate
    const time = lastUpdate ? Date.parse(lastUpdate) : Number.NaN

    if (!Number.isNaN(time) && time > bestTime) {
      best = index
      bestTime = time
    }
  })

  return best
}

export const toStudyView = (study: Study): DetailView => {
  const rawSessions = study.sessions ?? []
  const sessions = rawSessions.map((session, index) => toSession(study.id, session, index))
  const resumeIndex = lastPlayedIndex(rawSessions)
  const startIndex = resumeIndex >= 0 ? resumeIndex : 0

  return {
    id: study.id,
    title: study.title?.trim() || UNTITLED_STUDY,
    metaLine: formatMetaLine([
      study.productionYear?.toString(),
      study.ministryArea ?? undefined,
      formatAuthors(study.authors),
    ]),
    description: study.description?.trim() || undefined,
    backdropUrl: study.images?.posterUrl ?? study.images?.thumbnailUrl ?? sessions[0]?.thumbnailUrl,
    sessions,
    startHref: sessions[startIndex]?.href,
    hasProgress: resumeIndex >= 0,
  }
}
